import { useEffect, useMemo, useState } from 'react';
import { AppLayout } from '../../components/layout';
import { Button, Card, CopyButton, EmptyState, Input, MetricCard, Modal, Select } from '../../components/core';
import { DataTable, SpinnerBlock } from '../../components/feedback';
import { http } from '../../lib/api';
import { ADMIN_NAV } from './nav';
import type { AdminRecordingDto } from '@livestreamx/shared-types';

const fmtBytes = (b: number): string => {
  const u = ['B', 'KB', 'MB', 'GB'];
  let v = b;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${i === 0 ? v : v.toFixed(1)} ${u[i]}`;
};

const fmtWhen = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Absolute URL — the only thing worth copying, since the file is same-origin. */
const absolute = (url: string): string => `${window.location.origin}${url}`;

export function AdminRecordingsPage() {
  const [rows, setRows] = useState<AdminRecordingDto[] | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [streamId, setStreamId] = useState('');
  const [playing, setPlaying] = useState<AdminRecordingDto | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = () => {
    http
      .get<AdminRecordingDto[]>('/api/admin/recordings')
      .then((d) => {
        setRows(d);
        setError('');
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Failed to load recordings');
        setRows([]);
      });
  };

  const remove = async (r: AdminRecordingDto) => {
    if (!confirm(`Delete ${r.name}? This cannot be undone.`)) return;
    const key = `${r.streamId}:${r.name}`;
    setDeleting(key);
    try {
      await http.delete(`/api/admin/recordings/${r.streamId}/${encodeURIComponent(r.name)}`);
      setRows((prev) => (prev ?? []).filter((x) => !(x.streamId === r.streamId && x.name === r.name)));
      if (playing && playing.streamId === r.streamId && playing.name === r.name) setPlaying(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete recording');
    } finally {
      setDeleting(null);
    }
  };

  useEffect(() => {
    void load();
  }, []);



  const list = rows ?? [];
  const tenants = useMemo(
    () => [...new Map(list.map((r) => [r.tenantId, r.tenantName])).entries()].sort((a, b) => a[1].localeCompare(b[1])),
    [list],
  );
  const streams = useMemo(
    () => [...new Map(list.map((r) => [r.streamId, r.streamName])).entries()].sort((a, b) => a[1].localeCompare(b[1])),
    [list],
  );
  const q = search.trim().toLowerCase();
  const visible = list.filter(
    (r) =>
      (tenantId === '' || r.tenantId === tenantId) &&
      (streamId === '' || r.streamId === streamId) &&
      (q === '' ||
        r.name.toLowerCase().includes(q) ||
        r.streamName.toLowerCase().includes(q) ||
        r.tenantName.toLowerCase().includes(q)),
  );
  const totalBytes = visible.reduce((n, r) => n + r.sizeBytes, 0);

  const groups = useMemo(() => {
    const sorted = [...visible].sort(
      (a, b) =>
        a.tenantName.localeCompare(b.tenantName) ||
        a.streamName.localeCompare(b.streamName) ||
        b.recordedAt.localeCompare(a.recordedAt),
    );
    const byTenant = new Map<string, AdminRecordingDto[]>();
    for (const r of sorted) {
      const rowsFor = byTenant.get(r.tenantId) ?? [];
      rowsFor.push(r);
      byTenant.set(r.tenantId, rowsFor);
    }
    return [...byTenant.entries()].map(([id, tenantRows]) => {
      const byStream = new Map<string, AdminRecordingDto[]>();
      for (const r of tenantRows) {
        const rowsFor = byStream.get(r.streamId) ?? [];
        rowsFor.push(r);
        byStream.set(r.streamId, rowsFor);
      }
      return {
        tenantId: id,
        tenantName: tenantRows[0].tenantName,
        files: tenantRows.length,
        bytes: tenantRows.reduce((n, r) => n + r.sizeBytes, 0),
        streams: [...byStream.entries()].map(([sid, streamRows]) => ({
          streamId: sid,
          streamName: streamRows[0].streamName,
          rows: streamRows,
        })),
      };
    });
  }, [visible]);



  const columns = [
    { key: 'file', header: 'File', render: (r: AdminRecordingDto) => <span className="sd-mono">{r.name}</span> },
    { key: 'size', header: 'Size', render: (r: AdminRecordingDto) => fmtBytes(r.sizeBytes) },
    { key: 'when', header: 'Date / time', render: (r: AdminRecordingDto) => fmtWhen(r.recordedAt) },
    {
      key: 'actions',
      header: '',
      render: (r: AdminRecordingDto) => (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setPlaying(r)}>▶ Play</Button>
          <a className="btn secondary sm" href={r.url} download={r.name}>⬇ Download</a>
          <CopyButton text={absolute(r.url)} label="Copy link" />
          <a className="btn ghost sm" href={r.url} target="_blank" rel="noreferrer" title="Open in a new tab">↗</a>
          <Button
            size="sm"
            variant="danger"
            disabled={deleting === `${r.streamId}:${r.name}`}
            onClick={() => void remove(r)}
          >
            {deleting === `${r.streamId}:${r.name}` ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      ),
    },
  ];



  return (
    <AppLayout brand="LiveStreamX" navGroups={ADMIN_NAV} variant="admin">
      <div className="admin-page">
        <div className="page-header admin-page-header">
          <div>
            <h1 className="page-title">Recordings</h1>
            <p className="page-subtitle">Every recorded file across every tenant, by tenant and stream</p>
          </div>
          <Button variant="secondary" onClick={load}>Refresh</Button>
        </div>

        {error ? <div className="alert danger">{error}</div> : null}
        {!rows && !error ? <SpinnerBlock label="Loading recordings…" /> : null}

        {rows && rows.length > 0 ? (
          <>
            <div className="stats-grid">
              <MetricCard label="Files" value={visible.length} hint={`${list.length} total`} />
              <MetricCard label="Total size" value={fmtBytes(totalBytes)} hint="Of the current filter" />
              <MetricCard label="Tenants" value={tenants.length} hint="With recordings on file" />
              <MetricCard label="Streams" value={streams.length} hint="With recordings on file" />
            </div>

            <div className="toolbar">
              <div className="field grow">
                <Input value={search} onChange={setSearch} placeholder="Search file, stream or tenant…" />
              </div>
              <div className="field" style={{ minWidth: 180 }}>
                <Select
                  value={tenantId}
                  onChange={setTenantId}
                  options={[{ value: '', label: 'All tenants' }, ...tenants.map(([id, name]) => ({ value: id, label: name }))]}
                />
              </div>
              <div className="field" style={{ minWidth: 180 }}>
                <Select
                  value={streamId}
                  onChange={setStreamId}
                  options={[{ value: '', label: 'All streams' }, ...streams.map(([id, name]) => ({ value: id, label: name }))]}
                />
              </div>
            </div>

            {visible.length === 0 ? (
              <Card><EmptyState title="Nothing matches" body="No recording matches this filter." /></Card>
            ) : (
              groups.map((t) => (
                <Card
                  key={t.tenantId}
                  title={t.tenantName}
                  action={<span className="text-muted text-sm">{t.files} file{t.files === 1 ? '' : 's'} · {fmtBytes(t.bytes)}</span>}
                >
                  {t.streams.map((s) => (
                    <div key={s.streamId} className="mb-4">
                      <h4 className="card-title" style={{ margin: '0 0 0.5rem' }}>{s.streamName}</h4>
                      <DataTable<AdminRecordingDto> rows={s.rows} columns={columns} />
                    </div>
                  ))}
                </Card>
              ))
            )}
          </>
        ) : null}

        {rows && rows.length === 0 && !error ? (
          <EmptyState title="No recordings yet" body="Files appear here as soon as any tenant's encoder connects." />
        ) : null}

        {playing ? (
          <Modal title={`${playing.tenantName} · ${playing.streamName} — ${playing.name}`} onClose={() => setPlaying(null)}>
            <div className="sd-player">
              <video src={playing.url} controls autoPlay playsInline className="sd-player-video" />
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <CopyButton text={absolute(playing.url)} label="Copy link" />
              <a className="btn secondary" href={playing.url} download={playing.name}>
                ⬇ Download ({fmtBytes(playing.sizeBytes)})
              </a>
            </div>
          </Modal>
        ) : null}
      </div>
    </AppLayout>
  );
}
